import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-form-regis-appraiser',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './form-regis-appraiser.component.html',
  styleUrl: './form-regis-appraiser.component.scss'
})
export class FormRegisAppraiserComponent implements OnInit, OnDestroy {
  registrationForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  isSubmitting = false;
  priceSetup: any = null;
  isLoadingPrice = false;

  // File upload tracking
  uploadedFile: File | null = null;
  uploadedFileUrl: string | null = null;
  isUploadingFile = false;
  isDragOver = false;

  requiredDocLabels = [
    'สำเนาบัตรประชาชน (รับรองสำเนาถูกต้อง)',
    'สำเนาทะเบียนบ้าน',
    'สำเนาใบสำคัญการเปลี่ยนชื่อตัวหรือชื่อสกุล (ถ้ามี)',
    'สำเนาบัตรประจำตัวผู้ประเมิน',
    'สำเนาวุฒิบัตรหรือเอกสารแสดงผลการศึกษา',
    'สำเนาใบสำคัญผลการอบรมจากสมาคมวิชาชีพการประเมินมูลค่าทรัพย์สิน',
    'เอกสารแสดงผลการสอบผ่านการทดสอบความรู้ทางวิชาชีพของผู้ประเมิน',
    'เอกสารรับรองผลงานหรือประสบการณ์',
    'หลักฐานการบันทึกผลงานการประเมินมูลค่าทรัพย์สิน (Valuation Log Book)',
    'รูปถ่าย 2 นิ้ว หน้าตรง ไม่เกิน 6 เดือน สำหรับ ทำบัตร',
    'ผลการปฏิบัติงานที่เกี่ยวข้องกับการประเมินมูลค่าทรัพย์สิน (Valuation Log)'
  ];

  private subscription = new Subscription();

  // Dropdown options
  titleNames = [
    { value: 'นาย', label: 'นาย' },
    { value: 'นาง', label: 'นาง' },
    { value: 'นางสาว', label: 'นางสาว' }
  ];

  // Appraiser levels dropdown
  appraiserLevels = [
    { value: 'วิสามัญ', label: 'ชั้นวิสามัญ' },
    { value: 'สามัญ', label: 'ชั้นสามัญ' },
    { value: 'วุฒิ', label: 'ชั้นวุฒิ' }
  ];


  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadUserData();
    this.loadPriceSetup();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.registrationForm = this.fb.group({
      // Step 1: Appraiser Registration Information
      appraiserLevel: ['', Validators.required],
      requiredDocuments: [[]],

      // Step 2: Personal Information (Read-Only - loaded from user profile)
      titleName: [''],
      firstName: [''],
      lastName: [''],
      idCard: [''],
      dateOfBirth: [''],
      nationality: [''],
      phoneNumber: [''],
      lineId: [''],
      email: [''],

      // Current Address (Read-Only)
      currentHouseNumber: [''],
      currentVillage: [''],
      currentSoi: [''],
      currentRoad: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: [''],

      // Registered Address (Read-Only)
      registeredHouseNumber: [''],
      registeredVillage: [''],
      registeredSoi: [''],
      registeredRoad: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: [''],
      sameAsRegistered: [false]
    });
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps && step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    // Allow navigation for easier testing
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.registrationForm.get(fieldName);
    if (!control) return false;

    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }

    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.registrationForm.get(fieldName);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (control.errors['min']) return `ค่าต้องไม่น้อยกว่า ${control.errors['min'].min}`;
    if (control.errors['pattern']) {
      switch (fieldName) {
        case 'phoneNumber': return 'หมายเลขโทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
        case 'postalCode': return 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก';
        default: return 'รูปแบบข้อมูลไม่ถูกต้อง';
      }
    }

    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    if (this.isLoadingPrice) {
      alert('กรุณารอให้ระบบโหลดข้อมูลค่าธรรมเนียมเสร็จก่อนส่งข้อมูล');
      return;
    }

    if (!this.priceSetup) {
      alert('ไม่สามารถดึงข้อมูลค่าธรรมเนียมได้ กรุณาลองใหม่อีกครั้ง');
      return;
    }

    if (this.isUploadingFile) {
      alert('กรุณารอให้การอัพโหลดไฟล์เสร็จสมบูรณ์ก่อนส่งข้อมูล');
      return;
    }

    if (this.registrationForm.valid) {
      this.isSubmitting = true;

      // Get userId from currentUser in localStorage
      const currentUserStr = localStorage.getItem('currentUser');
      if (!currentUserStr) {
        alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่อีกครั้ง');
        this.isSubmitting = false;
        return;
      }

      const currentUser = JSON.parse(currentUserStr);
      const userId = currentUser.id;

      if (!userId) {
        alert('ไม่พบ User ID กรุณาเข้าสู่ระบบใหม่อีกครั้ง');
        this.isSubmitting = false;
        return;
      }

      // Build documents array from the single uploaded file
      const documentsArray = this.uploadedFileUrl ? [{
        name: this.uploadedFile?.name || 'เอกสารประกอบการพิจารณา',
        file: this.uploadedFileUrl
      }] : [];

      // Send as JSON (files already uploaded individually)
      const payload = {
        userId,
        appraiserLevel: this.registrationForm.get('appraiserLevel')?.value || '',
        priceSetupId: this.priceSetup.id,
        price: parseFloat(this.priceSetup.price),
        documents: documentsArray
      };

      // Send POST request to API
      const apiUrl = `${environment.apiUrl}/form-regis-appraiser`;

      this.http.post(apiUrl, payload).subscribe({
        next: (response: any) => {
          console.log('Form submitted successfully:', response);
          this.isSubmitting = false;

          // Show success message
          alert('ส่งคำขอขึ้นทะเบียนผู้ประเมินหลักสำเร็จ\n\nระบบจะดำเนินการพิจารณาคำขอของคุณและแจ้งผลกลับภายใน 7-14 วันทำการ');

          // Close modal with success response
          this.activeModal.close({
            success: true,
            data: response
          });
        },
        error: (error) => {
          console.error('Error submitting form:', error);
          this.isSubmitting = false;

          // Show error message
          const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการส่งข้อมูล กรุณาลองใหม่อีกครั้ง';
          alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.registrationForm.controls).forEach(key => {
        this.registrationForm.get(key)?.markAsTouched();
      });

      alert('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง');
    }
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลการสมัคร';
      case 2: return 'ข้อมูลทั่วไปและยืนยัน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-file-earmark-text';
      case 2: return 'bi-person-check';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Close modal
   */
  close(): void {
    this.activeModal.close();
  }

  /**
   * Dismiss modal
   */
  dismiss(): void {
    this.activeModal.dismiss();
  }

  // ==================== File Upload Methods ====================

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.setFile(input.files[0]);
    input.value = '';
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.uploadedFileUrl = null;
    this.isUploadingFile = false;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'bi-file-earmark-pdf-fill';
    if (['jpg', 'jpeg', 'png'].includes(ext ?? '')) return 'bi-file-image-fill';
    return 'bi-file-earmark-fill';
  }

  getFileIconClass(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'icon-pdf';
    if (['jpg', 'jpeg', 'png'].includes(ext ?? '')) return 'icon-image';
    return 'icon-other';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    this.setFile(files[0]);
  }

  private setFile(file: File): void {
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (file.size > maxSize) {
      alert(`ไฟล์ "${file.name}" มีขนาดใหญ่เกินไป กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 5MB`);
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      alert(`ไฟล์ "${file.name}" ประเภทไม่ถูกต้อง กรุณาเลือกไฟล์ประเภท PDF, JPG, JPEG หรือ PNG เท่านั้น`);
      return;
    }

    this.uploadedFile = file;
    this.uploadedFileUrl = null;
    this.isUploadingFile = true;

    const uploadData = new FormData();
    uploadData.append('file', file, file.name);

    this.http.post<any>(`${environment.apiUrl}/upload/image`, uploadData).subscribe({
      next: (response) => {
        this.uploadedFileUrl = response?.url || response?.data?.url || '';
        this.isUploadingFile = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        this.uploadedFile = null;
        this.uploadedFileUrl = null;
        this.isUploadingFile = false;
        this.cdr.detectChanges();
        const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการอัพโหลดไฟล์';
        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  // ==================== Review Section Helper Methods ====================

  /**
   * Get appraiser level label for review
   */
  getAppraiserLevelLabel(): string {
    const value = this.registrationForm.get('appraiserLevel')?.value;
    const level = this.appraiserLevels.find(e => e.value === value);
    return level?.label || '-';
  }

  // ==================== User Data Management ====================

  /**
   * Load user data from profile (mock data for now)
   * TODO: Replace with actual API call to fetch user profile
   */
  private loadUserData(): void {
    // Mock user data - replace this with actual API call
    const mockUserData = {
      titleName: 'นาย',
      firstName: 'ข้อมูลถูกดึงจากคอนทรีบิวเตอร์รันเกา',
      lastName: 'ข้อมูลถูกดึงจากคอนทรีบิวเตอร์รันเกา',
      idCard: '1234567890123',
      dateOfBirth: '1990-01-01',
      nationality: 'ไทย',
      phoneNumber: '0812345678',
      lineId: 'mylineid',
      email: 'example@email.com',

      // Registered Address
      registeredHouseNumber: '123',
      registeredVillage: 'หมู่ 1',
      registeredSoi: 'ซอย',
      registeredRoad: 'ถนน',
      registeredSubdistrict: 'ตำบล/แขวง',
      registeredDistrict: 'อำเภอ/เขต',
      registeredProvince: 'จังหวัด',
      registeredPostalCode: '12345',

      // Current Address
      currentHouseNumber: '123',
      currentVillage: 'หมู่ 1',
      currentSoi: 'ซอย',
      currentRoad: 'ถนน',
      currentSubdistrict: 'ตำบล/แขวง',
      currentDistrict: 'อำเภอ/เขต',
      currentProvince: 'จังหวัด',
      currentPostalCode: '12345',
      sameAsRegistered: false
    };

    // Populate form with user data
    this.registrationForm.patchValue(mockUserData, { emitEvent: false });

    // Disable all personal info fields to make them read-only
    this.disablePersonalInfoFields();
  }

  /**
   * Disable all personal information fields to make them read-only
   */
  private disablePersonalInfoFields(): void {
    const personalInfoFields = [
      'titleName', 'firstName', 'lastName', 'idCard', 'dateOfBirth', 'nationality',
      'phoneNumber', 'lineId', 'email',
      'registeredHouseNumber', 'registeredVillage', 'registeredSoi', 'registeredRoad',
      'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode',
      'currentHouseNumber', 'currentVillage', 'currentSoi', 'currentRoad',
      'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode',
      'sameAsRegistered'
    ];

    personalInfoFields.forEach(field => {
      this.registrationForm.get(field)?.disable({ emitEvent: false });
    });
  }

  private loadPriceSetup(): void {
    this.isLoadingPrice = true;
    this.http.get(`${environment.apiUrl}/price-setup/2ee6aa71-3e2e-4a2c-94de-08f1951d16ea`).subscribe({
      next: (data: any) => {
        this.priceSetup = data;
        this.isLoadingPrice = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoadingPrice = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const parts = [
      this.registrationForm.get(`${prefix}HouseNumber`)?.value,
      this.registrationForm.get(`${prefix}Village`)?.value,
      this.registrationForm.get(`${prefix}Soi`)?.value,
      this.registrationForm.get(`${prefix}Road`)?.value,
      this.registrationForm.get(`${prefix}Subdistrict`)?.value,
      this.registrationForm.get(`${prefix}District`)?.value,
      this.registrationForm.get(`${prefix}Province`)?.value,
      this.registrationForm.get(`${prefix}PostalCode`)?.value
    ].filter(part => part && part.trim() !== '');

    return parts.join(' ') || '-';
  }
}
